import React, { useMemo, useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../components/Navbar';
import SEO from '../components/SEO';

// Import images from assets
import tradeOnTVImage from '../assets/images/tradeontv.webp';
import tableOSImage from '../assets/images/tableosthumb.webp';
import tvAppsImage from '../assets/images/tv-apps-logo.webp';
import fitMediaImage from '../assets/images/fitmedia-logo.webp';
import restaurantAppsImage from '../assets/images/restaurant-apps-logo.webp';
import socialMediaMarketingImage from '../assets/images/social-media-marketing-logo.webp';
import socialMediaManagementImage from '../assets/images/social-media-management-logo.webp';
import allSoftware24Image from '../assets/images/all-software-24.webp';
import newsgatherImage from '../assets/images/newsgather-logo.webp';
import timeForFightImage from '../assets/images/timeforfight-logo.webp';

// Project type definition
interface ProjectType {
  id: number;
  title: string;
  titleTr: string;
  titleFr?: string;
  titleEs?: string;
  titleAr?: string;
  category: string;
  categoryTr: string;
  categoryFr?: string;
  categoryEs?: string;
  categoryAr?: string;
  image: string;
  description: string;
  descriptionTr: string;
  descriptionFr?: string;
  descriptionEs?: string;
  descriptionAr?: string;
  technologies: string[];
  year: string;
  link?: string;
}

// Styled components with AllSoftware24-inspired design
const ProjectsSection = styled.section`
  background: #f9f9f9;
  min-height: 100vh;
  padding: 80px 0;
  position: relative;
  overflow-x: hidden;
`;

const ProjectsContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  position: relative;
  z-index: 2;
`;

const ProjectsHeader = styled.div`
  text-align: center;
  margin-bottom: 60px;
`;

const PageTitle = styled(motion.h1)`
  font-size: 2.8rem;
  color: #333;
  margin-bottom: 20px;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  position: relative;
  
  &::after {
    content: '';
    display: block;
    width: 60px;
    height: 3px;
    background: #e74c3c;
    margin: 20px auto 0;
  }
`;

const PageSubtitle = styled(motion.p)`
  font-size: 1.1rem;
  color: #666;
  max-width: 700px;
  margin: 0 auto;
  line-height: 1.6;
`;

const ProjectsGrid = styled(motion.div)`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 30px;
  margin-bottom: 60px;
  
  @media (max-width: 992px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 576px) {
    grid-template-columns: 1fr;
    gap: 40px;
  }
`;

const ProjectCard = styled(motion.div)`
  background: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
  height: 100%;
  display: flex;
  flex-direction: column;
  border: none;
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 15px 30px rgba(0, 0, 0, 0.1);
  }
`;

// Create individual styled components for each project's image container
const ProjectImageContainer1 = styled.div`
  height: 220px;
  overflow: hidden;
  position: relative;
  background-color: #f0f0f0;
  display: flex;
  justify-content: center;
  align-items: center;
  
  @media (max-width: 768px) {
    height: 200px;
  }
`;

const ProjectImageContainer2 = styled.div`
  height: 250px;
  overflow: hidden;
  position: relative;
  background-color: #f0f0f0;
  display: flex;
  justify-content: center;
  align-items: center;
  
  @media (max-width: 768px) {
    height: 220px;
  }
`;

const ProjectImageContainer3 = styled.div`
  height: 200px;
  overflow: hidden;
  position: relative;
  background-color: #f0f0f0;
  display: flex;
  justify-content: center;
  align-items: center;
  
  @media (max-width: 768px) {
    height: 180px;
  }
`;

const ProjectImageContainer4 = styled.div`
  height: 230px;
  overflow: hidden;
  position: relative;
  background-color: #f0f0f0;
  display: flex;
  justify-content: center;
  align-items: center;
  
  @media (max-width: 768px) {
    height: 210px;
  }
`;

const ProjectImageContainer5 = styled.div`
  height: 220px;
  overflow: hidden;
  position: relative;
  background-color: #f0f0f0;
  display: flex;
  justify-content: center;
  align-items: center;
  
  @media (max-width: 768px) {
    height: 200px;
  }
`;

const ProjectImageContainer6 = styled.div`
  height: 210px;
  overflow: hidden;
  position: relative;
  background-color: #f0f0f0;
  display: flex;
  justify-content: center;
  align-items: center;
  
  @media (max-width: 768px) {
    height: 190px;
  }
`;

const ProjectImageContainer7 = styled.div`
  height: 240px;
  overflow: hidden;
  position: relative;
  background-color: #f0f0f0;
  display: flex;
  justify-content: center;
  align-items: center;
  
  @media (max-width: 768px) {
    height: 220px;
  }
`;

const ProjectImageContainer8 = styled.div`
  height: 220px;
  overflow: hidden;
  position: relative;
  background-color: #f0f0f0;
  display: flex;
  justify-content: center;
  align-items: center;
  
  @media (max-width: 768px) {
    height: 200px;
  }
`;

const ProjectImageContainer9 = styled.div`
  height: 220px;
  overflow: hidden;
  position: relative;
  background-color: #f0f0f0;
  display: flex;
  justify-content: center;
  align-items: center;
  
  @media (max-width: 768px) {
    height: 200px;
  }
`;

const ProjectImageContainer10 = styled.div`
  height: 220px;
  overflow: hidden;
  position: relative;
  background-color: #f0f0f0;
  display: flex;
  justify-content: center;
  align-items: center;
  
  @media (max-width: 768px) {
    height: 200px;
  }
`;

// Update the ProjectImage styled component
const ProjectImage = styled.img`
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  padding: 10px;
  transition: transform 0.5s ease;
  
  ${ProjectCard}:hover & {
    transform: scale(1.05);
  }
`;

// Remove the eslint warning by commenting out the unused styled component
// const ProjectYear = styled.span`
//   position: absolute;
//   top: 15px;
//   right: 15px;
//   background: #e74c3c;
//   color: white;
//   padding: 6px 12px;
//   border-radius: 20px;
//   font-size: 0.75rem;
//   font-weight: 500;
// `;

const ProjectContent = styled.div`
  padding: 25px;
  flex-grow: 1;
  display: flex;
  flex-direction: column;
`;

const ProjectTitle = styled.h3`
  font-size: 1.25rem;
  color: #333;
  margin-bottom: 12px;
  font-weight: 600;
  position: relative;
  
  &::after {
    content: '';
    display: block;
    width: 40px;
    height: 2px;
    background: #e74c3c;
    margin-top: 8px;
  }
`;

const ProjectDescription = styled.div`
  font-size: 0.95rem;
  color: #666;
  line-height: 1.6;
  margin-bottom: 20px;
  flex-grow: 1;
  
  strong {
    font-weight: 600;
  }
`;

const ProjectTechnologies = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 20px;
`;

const TechnologyTag = styled.span`
  font-size: 0.75rem;
  background: #f5f5f5;
  color: #666;
  padding: 5px 12px;
  border-radius: 15px;
  display: inline-block;
  border: 1px solid #eee;
`;

const FeaturedProjectSection = styled.div`
  margin-bottom: 80px;
`;

const SectionTitle = styled.h2`
  font-size: 1.8rem;
  color: #333;
  margin-bottom: 30px;
  font-weight: 600;
  position: relative;
  display: inline-block;
  
  &::after {
    content: '';
    display: block;
    width: 100%;
    height: 3px;
    background: #e74c3c;
    margin-top: 8px;
  }
`;

const FeaturedProject = styled.div`
  position: relative;
  background: white;
  color: #333;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 15px 40px rgba(0, 0, 0, 0.1);
`;

const FeaturedProjectContent = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0;
  position: relative;
  z-index: 1;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FeaturedProjectInfo = styled.div`
  display: flex;
  flex-direction: column;
  padding: 50px;
  
  @media (max-width: 768px) {
    padding: 30px;
  }
`;

const FeaturedProjectTitle = styled(motion.h2)`
  font-size: 2rem;
  color: #333;
  margin-bottom: 20px;
  font-weight: 600;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    width: 60px;
    height: 3px;
    background-color: #e74c3c;
    bottom: -10px;
    left: 0;
  }
`;

const FeaturedProjectDescription = styled.p`
  font-size: 1rem;
  color: #666;
  line-height: 1.7;
  margin-bottom: 30px;
`;

const FeaturedProjectTechnologies = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 30px;
`;

const FeaturedProjectLink = styled(motion.a)`
  display: inline-block;
  background: #e74c3c;
  color: white;
  padding: 12px 30px;
  border-radius: 30px;
  text-decoration: none;
  font-weight: 500;
  align-self: flex-start;
  letter-spacing: 1px;
  
  &:hover {
    background: #d44333;
    transform: translateY(-3px);
    box-shadow: 0 10px 20px rgba(231, 76, 60, 0.3);
  }
`;

const FeaturedProjectImageContainer = styled.div`
  height: 100%;
  min-height: 400px;
  overflow: hidden;
  background-color: #f0f0f0;
  display: flex;
  justify-content: center;
  align-items: center;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    max-height: 400px;
  }
  
  @media (max-width: 768px) {
    height: 300px;
    
    img {
      max-height: 300px;
    }
  }
`;

// Video styled components
const VideoElement = styled.video`
  width: 100%;
  height: auto;
  max-height: 80vh;
  object-fit: contain;
  background-color: #000;
  outline: none !important;
  pointer-events: none;
  
  @media (max-width: 768px) {
    width: 100%;
    height: auto;
  }
`;

const HeroVideoContainer = styled.div<{ isVisible: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.9);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  opacity: ${props => props.isVisible ? '1' : '0'};
  visibility: ${props => props.isVisible ? 'visible' : 'hidden'};
  transition: opacity 0.5s ease, visibility 0.5s ease;
`;

const VideoControls = styled.div`
  position: absolute;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 15px;
  z-index: 30;
  background-color: rgba(0, 0, 0, 0.6);
  padding: 8px 16px;
  border-radius: 8px;
`;

const VideoCloseBtn = styled.button`
  background: none;
  color: white;
  border: none;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: opacity 0.3s ease;
  
  &:hover {
    opacity: 0.8;
  }
`;

const VolumeControl = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  
  svg {
    margin-right: 5px;
  }
  
  input {
    width: 80px;
    cursor: pointer;
  }
`;

const WatchButton = styled(motion.button)`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background-color: #e74c3c;
  color: white;
  border: none;
  border-radius: 30px;
  padding: 12px 25px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  text-decoration: none;
  font-weight: 500;
  letter-spacing: 1px;
  
  &:hover {
    background: #d44333;
    transform: translateY(-3px);
    box-shadow: 0 10px 20px rgba(231, 76, 60, 0.3);
  }
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

// Video kalite seçici styled component
const QualitySelector = styled.select`
  background: transparent;
  color: white;
  border: none;
  outline: none;
  cursor: pointer;
  option { background: white; color: black; }
`;

// Projects component
const Projects = () => {
  const { language } = useLanguage();
  const isTurkish = language === 'tr';
  
  // Video state and refs
  const [showVideo, setShowVideo] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  
  // Video volume change handler
  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const volume = parseFloat(e.target.value);
    if (videoRef.current) {
      videoRef.current.volume = volume;
    }
  };
  
  // Video close handler
  const handleCloseVideo = (e?: React.MouseEvent<HTMLButtonElement> | Event) => {
    if (e && 'preventDefault' in e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0; // Reset video to start
    }
    
    setShowVideo(false);
  };
  
  // Play video handler
  const handlePlayVideo = () => {
    setShowVideo(true);
    
    // Configure and play video
    setTimeout(() => {
      if (videoRef.current) {
        try {
          // Reset video to start
          videoRef.current.currentTime = 0;
          
          // Set video properties
          videoRef.current.autoplay = true;
          videoRef.current.muted = false;
          videoRef.current.volume = 0.7;
          videoRef.current.controls = false;
          videoRef.current.playsInline = true;
          
          // Play video
          const playPromise = videoRef.current.play();
          
          if (playPromise !== undefined) {
            playPromise.then(() => {
              console.log("Video playing successfully");
            }).catch(error => {
              console.error("Video playback error:", error);
            });
          }
          
          // When video ends
          videoRef.current.onended = () => {
            handleCloseVideo();
          };
        } catch (error) {
          console.error("Video setup error:", error);
        }
      }
    }, 300);
  };
  
  // Video kalite state ve mapping
  const [quality, setQuality] = useState<'1080p' | '720p' | '360p'>('1080p');
  const videoSources: Record<string, string> = {
    '1080p': '/video-1080p.mp4',
    '720p': '/video-720p.mp4',
    '360p': '/video-360p.mp4',
  };
  const handleQualityChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newQ = e.target.value as '1080p' | '720p' | '360p';
    setQuality(newQ);
    if (videoRef.current) {
      const t = videoRef.current.currentTime;
      videoRef.current.src = videoSources[newQ];
      videoRef.current.load();
      videoRef.current.currentTime = t;
      const p = videoRef.current.play(); if (p !== undefined) p.catch(()=>{});
    }
  };
  
  // SEO metadata for Projects page
  const projectsSeoData = {
    en: {
      title: 'Projects - Barkin Sayin | Smart TV & Fintech Innovations',
      description: 'Explore Barkin Sayin\'s innovative projects including Trade on tv, the pioneering smart TV financial application, and other cutting-edge fintech solutions.',
      keywords: [
        'Barkin Sayin projects',
        'Trade on tv application',
        'fintech projects',
        'smart TV innovations',
        'financial technology solutions',
        'digital transformation projects',
        'tech entrepreneur portfolio'
      ]
    },
    tr: {
      title: 'Projeler - Barkın Sayın | Akıllı TV ve Fintech Yenilikleri',
      description: 'Barkın Sayın\'ın Trade on tv dahil olmak üzere öncü akıllı TV finansal uygulaması ve diğer yenilikçi fintech çözümlerini keşfedin.',
      keywords: [
        'Barkın Sayın projeleri',
        'Trade on tv uygulaması',
        'fintech projeleri',
        'akıllı TV yenilikleri',
        'finansal teknoloji çözümleri',
        'dijital dönüşüm projeleri',
        'teknoloji girişimcisi portfolyosu'
      ]
    }
  };
  
  const seoTitle = projectsSeoData[language as keyof typeof projectsSeoData]?.title || projectsSeoData.en.title;
  const seoDescription = projectsSeoData[language as keyof typeof projectsSeoData]?.description || projectsSeoData.en.description;
  const seoKeywords = projectsSeoData[language as keyof typeof projectsSeoData]?.keywords || projectsSeoData.en.keywords;
  
  // Sample projects data
  const projectsData: ProjectType[] = useMemo(() => [
    {
      id: 1,
      title: "Trade on tv",
      titleTr: "Trade on tv",
      titleFr: "Trade on tv",
      titleEs: "Trade on tv",
      titleAr: "Trade on tv",
      category: "FinTech",
      categoryTr: "FinTek",
      categoryFr: "FinTech",
      categoryEs: "FinTech",
      categoryAr: "التكنولوجيا المالية",
      image: tradeOnTVImage,
      description: "The world's first finance-focused Smart TV app. It offers traders on-screen trading power with AI-powered analytics.<br/><br/>• Trade on tv is the world's first smart TV app that brings financial information to the TV.<br/>• Investors can now track stocks, cryptocurrencies, foreign exchange and indices in real time.<br/>• Get AI-powered analysis and manage portfolios on screen.",
      descriptionTr: "Uygulamamız, kullanıcıların borsa ve kripto para birimlerinin mevcut durumunu doğrudan televizyonlarından takip etmelerini sağlar. Kullanıcılar, favori hisse senetlerini ve kripto para birimlerini seçebilir, piyasa hareketlerini gerçek zamanlı olarak izleyebilir ve önemli finansal göstergelere erişebilirler. Kullanıcı dostu arayüzü ile uygulamamız, televizyonlarını kullanarak borsa ve kripto para piyasalarını takip etmek isteyen kullanıcılara sorunsuz bir deneyim sunmak için tasarlanmıştır.",
      descriptionFr: "La première application Smart TV axée sur la finance au monde. Elle offre aux traders une puissance de trading à l'écran avec des analyses alimentées par l'IA.<br/><br/>• Trade on tv est la première application de télévision intelligente au monde qui apporte des informations financières à la télévision.<br/>• Les investisseurs peuvent désormais suivre les actions, les crypto-monnaies, les changes et les indices en temps réel.<br/>• Obtenez des analyses alimentées par l'IA et gérez des portefeuilles à l'écran.",
      descriptionEs: "La primera aplicación Smart TV del mundo centrada en las finanzas. Ofrece a los operadores poder de negociación en pantalla con análisis impulsados por IA.<br/><br/>• Trade on tv es la primera aplicación de televisión inteligente del mundo que lleva información financiera al televisor.<br/>• Los inversores ahora pueden seguir acciones, criptomonedas, divisas e índices en tiempo real.<br/>• Obtenga análisis impulsados por IA y administre carteras en pantalla.",
      descriptionAr: "أول تطبيق تلفزيون ذكي في العالم يركز على التمويل. يوفر للمتداولين قوة تداول على الشاشة مع تحليلات مدعومة بالذكاء الاصطناعي.<br/><br/>• Trade on tv هو أول تطبيق تلفزيون ذكي في العالم يجلب المعلومات المالية إلى التلفزيون.<br/>• يمكن للمستثمرين الآن تتبع الأسهم والعملات المشفرة وصرف العملات والمؤشرات في الوقت الحقيقي.<br/>• احصل على تحليلات مدعومة بالذكاء الاصطناعي وإدارة المحافظ على الشاشة.",
      technologies: ["React", "Node.js", "WebSocket", "TradingView API", "OAuth 2.0", "Smart TV SDKs", "Secure Payment Gateway"],
      year: "2021",
      link: "https://tradeontv.com/"
    },
    {
      id: 2,
      title: "TableOS - GastroDesk",
      titleTr: "TableOS - GastroDesk",
      titleFr: "TableOS - GastroDesk",
      titleEs: "TableOS - GastroDesk",
      titleAr: "TableOS - GastroDesk",
      category: "Hospitality",
      categoryTr: "Konaklama",
      categoryFr: "Hôtellerie",
      categoryEs: "Hostelería",
      categoryAr: "الضيافة",
      image: tableOSImage,
      description: "A comprehensive kiosk solution built for restaurants, allowing customers to browse menu items, place orders, and make payments directly from their table. The intuitive interface facilitates easy navigation through menu categories and customization options, while the backend provides robust order management and analytics for restaurant staff. This solution can integrate with existing POS systems and payment gateways, streamlining restaurant operations and enhancing customer satisfaction.",
      descriptionTr: "Restoranlar için geliştirilmiş kapsamlı bir kiosk çözümü, müşterilerin menü öğelerini görüntülemelerine, sipariş vermelerine ve ödemelerini doğrudan masalarından yapmalarına olanak tanır. Sezgisel arayüz, menü kategorileri ve özelleştirme seçenekleri arasında kolay gezinmeyi sağlarken, arka uç restoran personeline güçlü sipariş yönetimi ve analitik sağlar. Bu çözüm, mevcut POS sistemleri ve ödeme ağ geçitleri ile entegre olabilir, restoran operasyonlarını kolaylaştırır ve müşteri memnuniyetini artırır.",
      descriptionFr: "Une solution de kiosque complète conçue pour les restaurants, permettant aux clients de parcourir les articles du menu, de passer des commandes et d'effectuer des paiements directement depuis leur table. L'interface intuitive facilite la navigation à travers les catégories de menu et les options de personnalisation, tandis que le backend fournit une gestion des commandes robuste et des analyses pour le personnel du restaurant. Cette solution peut s'intégrer aux systèmes POS existants et aux passerelles de paiement, rationalisant les opérations du restaurant et améliorant la satisfaction des clients.",
      descriptionEs: "Una solución integral de quiosco construida para restaurantes, que permite a los clientes navegar por los elementos del menú, realizar pedidos y efectuar pagos directamente desde su mesa. La interfaz intuitiva facilita la navegación a través de las categorías del menú y las opciones de personalización, mientras que el backend proporciona una gestión de pedidos robusta y análisis para el personal del restaurante. Esta solución puede integrarse con los sistemas POS existentes y pasarelas de pago, agilizando las operaciones del restaurante y mejorando la satisfacción del cliente.",
      descriptionAr: "حل كشك شامل مبني للمطاعم، يسمح للعملاء بتصفح عناصر القائمة وتقديم الطلبات وإجراء المدفوعات مباشرة من طاولتهم. تسهل الواجهة البديهية التنقل السهل عبر فئات القائمة وخيارات التخصيص، بينما يوفر الجزء الخلفي إدارة قوية للطلبات وتحليلات لموظفي المطعم. يمكن لهذا الحل الاندماج مع أنظمة نقاط البيع الحالية وبوابات الدفع، مما يبسط عمليات المطعم ويعزز رضا العملاء.",
      technologies: ["React Native", "Node.js", "PostgreSQL", "Socket.io", "Stripe API", "Kitchen Display System", "Restaurant POS Integration"],
      year: "2022",
      link: "https://tradeontv.com/"
    },
    {
      id: 3,
      title: "TV Apps",
      titleTr: "TV Uygulamaları",
      titleFr: "Applications TV",
      titleEs: "Aplicaciones para TV",
      titleAr: "تطبيقات التلفزيون",
      category: "Media & Entertainment",
      categoryTr: "Medya ve Eğlence",
      categoryFr: "Médias et Divertissement",
      categoryEs: "Medios y Entretenimiento",
      categoryAr: "الإعلام والترفيه",
      image: tvAppsImage,
      description: "Custom Smart TV applications designed for Samsung Tizen, LG WebOS, and Android TV platforms. These applications enable content providers to distribute their media directly to Smart TV users, offering features such as live streaming, on-demand video playback, user authentication, and personalized recommendations. Our TV apps provide seamless cross-platform functionality while maintaining high performance and compliance with each platform's specific requirements and guidelines.",
      descriptionTr: "Samsung Tizen, LG WebOS ve Android TV platformları için özel olarak tasarlanmış Akıllı TV uygulamaları. Bu uygulamalar, içerik sağlayıcıların medyalarını doğrudan Akıllı TV kullanıcılarına dağıtmalarını sağlar ve canlı yayın, isteğe bağlı video oynatma, kullanıcı kimlik doğrulama ve kişiselleştirilmiş öneriler gibi özellikler sunar. TV uygulamalarımız, her platformun belirli gereksinimlerine ve yönergelerine uygun olarak yüksek performans sağlarken, sorunsuz bir çapraz platform işlevselliği sunar.",
      descriptionFr: "Applications Smart TV personnalisées conçues pour les plateformes Samsung Tizen, LG WebOS et Android TV. Ces applications permettent aux fournisseurs de contenu de distribuer leurs médias directement aux utilisateurs de Smart TV, offrant des fonctionnalités telles que la diffusion en direct, la lecture vidéo à la demande, l'authentification des utilisateurs et des recommandations personnalisées. Nos applications TV offrent une fonctionnalité multiplateforme transparente tout en maintenant des performances élevées et la conformité aux exigences et directives spécifiques de chaque plateforme.",
      descriptionEs: "Aplicaciones personalizadas para Smart TV diseñadas para las plataformas Samsung Tizen, LG WebOS y Android TV. Estas aplicaciones permiten a los proveedores de contenido distribuir sus medios directamente a los usuarios de Smart TV, ofreciendo características como transmisión en vivo, reproducción de video bajo demanda, autenticación de usuarios y recomendaciones personalizadas. Nuestras aplicaciones para TV proporcionan una funcionalidad multiplataforma sin problemas mientras mantienen un alto rendimiento y el cumplimiento de los requisitos y directrices específicos de cada plataforma.",
      descriptionAr: "تطبيقات تلفزيون ذكية مخصصة مصممة لمنصات Samsung Tizen و LG WebOS و Android TV. تمكن هذه التطبيقات مزودي المحتوى من توزيع وسائطهم مباشرة إلى مستخدمي التلفزيون الذكي، وتقدم ميزات مثل البث المباشر وتشغيل الفيديو حسب الطلب ومصادقة المستخدم والتوصيات الشخصية. توفر تطبيقات التلفزيون لدينا وظائف متعددة المنصات بسلاسة مع الحفاظ على أداء عالٍ والامتثال للمتطلبات والإرشادات الخاصة بكل منصة.",
      technologies: ["Tizen SDK", "WebOS SDK", "Android TV SDK", "React", "HLS/DASH Streaming", "DRM Integration", "RESTful APIs"],
      year: "2022",
      link: "https://tradeontv.com/"
    },
    {
      id: 4,
      title: "FitMedia Player",
      titleTr: "FitMedia Player",
      titleFr: "FitMedia Player",
      titleEs: "FitMedia Player",
      titleAr: "FitMedia Player",
      category: "Multimedia",
      categoryTr: "Multimedya",
      categoryFr: "Multimédia",
      categoryEs: "Multimedia",
      categoryAr: "الوسائط المتعددة",
      image: fitMediaImage,
      description: "FitMedia Player is a user-friendly mobile application that can play various media formats. With its intuitive interface, users can easily manage and play their media files, creating a seamless entertainment experience.",
      descriptionTr: "FitMedia Player, çeşitli medya formatlarını oynatabilecek kullanıcı dostu bir mobil uygulamadır. Sezgisel arayüzü sayesinde, kullanıcılar medya dosyalarını kolayca yönetebilir ve oynatabilir, kesintisiz bir eğlence deneyimi yaratabilirler.",
      descriptionFr: "FitMedia Player est une application mobile conviviale qui peut lire divers formats multimédia. Grâce à son interface intuitive, les utilisateurs peuvent facilement gérer et lire leurs fichiers multimédia, créant ainsi une expérience de divertissement transparente.",
      descriptionEs: "FitMedia Player es una aplicación móvil fácil de usar que puede reproducir varios formatos multimedia. Con su interfaz intuitiva, los usuarios pueden administrar y reproducir fácilmente sus archivos multimedia, creando una experiencia de entretenimiento perfecta.",
      descriptionAr: "FitMedia Player هو تطبيق جوال سهل الاستخدام يمكنه تشغيل تنسيقات وسائط متنوعة. من خلال واجهته البديهية، يمكن للمستخدمين إدارة وتشغيل ملفات الوسائط الخاصة بهم بسهولة، مما يخلق تجربة ترفيهية سلسة.",
      technologies: ["Electron", "FFmpeg", "React", "Redux"],
      year: "2022",
      link: "https://tradeontv.com/"
    },
    {
      id: 5,
      title: "Restaurant Apps",
      titleTr: "Restoran Uygulamaları",
      titleFr: "Applications de Restaurant",
      titleEs: "Aplicaciones para Restaurantes",
      titleAr: "تطبيقات المطاعم",
      category: "Hospitality",
      categoryTr: "Konaklama",
      categoryFr: "Hôtellerie",
      categoryEs: "Hostelería",
      categoryAr: "الضيافة",
      image: restaurantAppsImage,
      description: "We specialize in developing restaurant applications, including digital ordering and billing systems. Our app streamlines the dining experience by allowing customers to browse menus, place orders, and pay bills directly from their smartphones. With our intuitive interface and seamless integration, restaurant owners can enhance efficiency and provide a more convenient service to their customers.",
      descriptionTr: "Dijital sipariş ve fatura sistemleri dahil olmak üzere restoran uygulamaları geliştirme konusunda uzmanız. Uygulamamız, müşterilerin menülere göz atmasına, sipariş vermesine ve faturaları doğrudan akıllı telefonlarından ödemesine olanak tanıyarak yemek deneyimini kolaylaştırır. Sezgisel arayüzümüz ve sorunsuz entegrasyonumuz sayesinde, restoran sahipleri verimliliği artırabilir ve müşterilerine daha uygun bir hizmet sunabilirler.",
      descriptionFr: "Nous sommes spécialisés dans le développement d'applications de restaurant, y compris les systèmes de commande et de facturation numériques. Notre application simplifie l'expérience de restauration en permettant aux clients de parcourir les menus, de passer des commandes et de payer des factures directement depuis leurs smartphones. Grâce à notre interface intuitive et à notre intégration transparente, les propriétaires de restaurants peuvent améliorer l'efficacité et offrir un service plus pratique à leurs clients.",
      descriptionEs: "Nos especializamos en el desarrollo de aplicaciones para restaurantes, incluidos sistemas digitales de pedidos y facturación. Nuestra aplicación agiliza la experiencia gastronómica al permitir a los clientes navegar por los menús, realizar pedidos y pagar facturas directamente desde sus teléfonos inteligentes. Con nuestra interfaz intuitiva e integración perfecta, los propietarios de restaurantes pueden mejorar la eficiencia y brindar un servicio más conveniente a sus clientes.",
      descriptionAr: "نحن متخصصون في تطوير تطبيقات المطاعم، بما في ذلك أنظمة الطلب والفوترة الرقمية. يعمل تطبيقنا على تبسيط تجربة تناول الطعام من خلال السماح للعملاء بتصفح القوائم وتقديم الطلبات وإجراء المدفوعات مباشرة من هواتفهم الذكية. من خلال واجهتنا البديهية والتكامل السلس، يمكن لأصحاب المطاعم تعزيز الكفاءة وتقديم خدمة أكثر ملاءمة لعملائهم.",
      technologies: ["React Native", "Firebase", "Stripe API", "Node.js"],
      year: "2021",
      link: "https://tradeontv.com/"
    },
    {
      id: 6,
      title: "Social Media Marketing",
      titleTr: "Sosyal Medya Pazarlaması",
      titleFr: "Marketing des Médias Sociaux",
      titleEs: "Marketing en Redes Sociales",
      titleAr: "تسويق وسائل التواصل الاجتماعي",
      category: "Marketing",
      categoryTr: "Pazarlama",
      categoryFr: "Marketing",
      categoryEs: "Marketing",
      categoryAr: "التسويق",
      image: socialMediaMarketingImage,
      description: "We craft targeted social media marketing campaigns across platforms like Facebook, Instagram, and LinkedIn to boost brand awareness and engagement.",
      descriptionTr: "Marka bilinirliğini ve etkileşimi artırmak için Facebook, Instagram ve LinkedIn gibi platformlarda hedefli sosyal medya pazarlama kampanyaları oluşturuyoruz.",
      descriptionFr: "Nous élaborons des campagnes de marketing sur les réseaux sociaux ciblées sur des plateformes comme Facebook, Instagram et LinkedIn pour augmenter la notoriété de la marque et l'engagement.",
      descriptionEs: "Creamos campañas de marketing en redes sociales dirigidas en plataformas como Facebook, Instagram y LinkedIn para aumentar el conocimiento de la marca y el compromiso.",
      descriptionAr: "نقوم بإنشاء حملات تسويقية مستهدفة على وسائل التواصل الاجتماعي عبر منصات مثل فيسبوك وإنستغرام ولينكد إن لتعزيز الوعي بالعلامة التجارية والمشاركة.",
      technologies: ["HootSuite", "Buffer", "Analytics Tools", "Content Management"],
      year: "2023",
      link: "https://tradeontv.com/"
    },
    {
      id: 7,
      title: "Social Media Management",
      titleTr: "Sosyal Medya Yönetimi",
      titleFr: "Gestion des Médias Sociaux",
      titleEs: "Gestión de Redes Sociales",
      titleAr: "إدارة وسائل التواصل الاجتماعي",
      category: "Marketing",
      categoryTr: "Pazarlama",
      categoryFr: "Marketing",
      categoryEs: "Marketing",
      categoryAr: "التسويق",
      image: socialMediaManagementImage,
      description: "Our social media management service handles content creation, scheduling, and community engagement to maintain a consistent brand presence online.",
      descriptionTr: "Sosyal medya yönetim hizmetimiz, çevrimiçi ortamda tutarlı bir marka varlığı sürdürmek için içerik oluşturma, zamanlama ve topluluk etkileşimi ile ilgilenir.",
      descriptionFr: "Notre service de gestion des médias sociaux s'occupe de la création de contenu, de la planification et de l'engagement communautaire pour maintenir une présence de marque cohérente en ligne.",
      descriptionEs: "Nuestro servicio de gestión de redes sociales se encarga de la creación de contenido, programación y participación de la comunidad para mantener una presencia de marca consistente en línea.",
      descriptionAr: "تتعامل خدمة إدارة وسائل التواصل الاجتماعي لدينا مع إنشاء المحتوى والجدولة ومشاركة المجتمع للحفاظ على تواجد متسق للعلامة التجارية عبر الإنترنت.",
      technologies: ["Sprout Social", "Canva", "Facebook Business", "Google Analytics"],
      year: "2023",
      link: "https://tradeontv.com/"
    },
    {
      id: 8,
      title: "Newsgather",
      titleTr: "Newsgather",
      titleFr: "Newsgather",
      titleEs: "Newsgather",
      titleAr: "Newsgather",
      category: "Media & News Tech",
      categoryTr: "Medya ve Haber Teknolojisi",
      categoryFr: "Médias et Tech Actualités",
      categoryEs: "Medios y Tecnología de Noticias",
      categoryAr: "الإعلام وتقنية الأخبار",
      image: newsgatherImage,
      description: "Newsgather is an AI-powered global news intelligence platform that aggregates breaking stories from thousands of sources worldwide. It delivers real-time headlines, multilingual coverage, sentiment analysis, and curated daily briefings across categories such as world news, finance, technology, sports, and health.<br/><br/>• Live breaking-news ticker with multi-language headlines<br/>• AI-assisted summaries, sentiment scoring, and trend detection<br/>• Country-based coverage and personalized news feeds<br/>• Daily newsletter with editor-selected top stories",
      descriptionTr: "Newsgather, dünya genelinde binlerce kaynaktan son dakika haberlerini toplayan yapay zeka destekli küresel bir haber zekası platformudur. Gerçek zamanlı manşetler, çok dilli kapsam, duygu analizi ve dünya, finans, teknoloji, spor ve sağlık gibi kategorilerde seçilmiş günlük bültenler sunar.<br/><br/>• Çok dilli son dakika haber şeridi<br/>• Yapay zeka destekli özetler, duygu skorları ve trend tespiti<br/>• Ülkeye göre kapsam ve kişiselleştirilmiş haber akışları<br/>• Editör seçimiyle günlük haber bülteni",
      descriptionFr: "Newsgather est une plateforme mondiale d'intelligence médiatique alimentée par l'IA qui agrège les actualités de milliers de sources. Elle propose des titres en temps réel, une couverture multilingue, une analyse de sentiment et des briefings quotidiens dans les catégories monde, finance, technologie, sport et santé.",
      descriptionEs: "Newsgather es una plataforma global de inteligencia de noticias impulsada por IA que agrega historias de miles de fuentes. Ofrece titulares en tiempo real, cobertura multilingüe, análisis de sentimiento y resúmenes diarios en categorías como mundo, finanzas, tecnología, deportes y salud.",
      descriptionAr: "Newsgather منصة عالمية لذكاء الأخبار مدعومة بالذكاء الاصطناعي تجمع الأخبار العاجلة من آلاف المصادر. توفر عناوين فورية وتغطية متعددة اللغات وتحليل المشاعر ونشرات يومية في فئات مثل العالم والمالية والتكنولوجيا والرياضة والصحة.",
      technologies: ["React", "Next.js", "Node.js", "AI/ML", "NLP", "Real-time APIs", "Multi-language CMS"],
      year: "2025",
      link: "https://newsgather.com/"
    },
    {
      id: 9,
      title: "AllSoftware24",
      titleTr: "AllSoftware24",
      titleFr: "AllSoftware24",
      titleEs: "AllSoftware24",
      titleAr: "AllSoftware24",
      category: "Software Development",
      categoryTr: "Yazılım Geliştirme",
      categoryFr: "Développement Logiciel",
      categoryEs: "Desarrollo de Software",
      categoryAr: "تطوير البرمجيات",
      image: allSoftware24Image,
      description: "AllSoftware24 is a trusted partner for innovative software solutions, delivering custom web and mobile applications, AI-driven products, and end-to-end digital transformation. The platform showcases flagship projects including Smart TV fintech apps, restaurant kiosk systems, and cross-platform TV applications.<br/><br/>• Custom web and mobile development for global clients<br/>• AI and machine learning integration for intelligent products<br/>• Smart TV, kiosk, and enterprise software expertise<br/>• Full-cycle delivery from concept to deployment and support",
      descriptionTr: "AllSoftware24, özel web ve mobil uygulamalar, yapay zeka destekli ürünler ve uçtan uca dijital dönüşüm sunan yenilikçi yazılım çözümleri ortağıdır. Platform; akıllı TV fintech uygulamaları, restoran kiosk sistemleri ve çapraz platform TV uygulamaları gibi öncü projeleri sergiler.<br/><br/>• Küresel müşteriler için özel web ve mobil geliştirme<br/>• Akıllı ürünler için yapay zeka ve makine öğrenimi entegrasyonu<br/>• Akıllı TV, kiosk ve kurumsal yazılım uzmanlığı<br/>• Fikirden canlıya ve desteğe kadar uçtan uca teslimat",
      descriptionFr: "AllSoftware24 est un partenaire de confiance pour des solutions logicielles innovantes : applications web et mobiles sur mesure, produits IA et transformation numérique de bout en bout, avec des projets phares en fintech TV, kiosques restauration et applications Smart TV.",
      descriptionEs: "AllSoftware24 es un socio de confianza en soluciones de software innovadoras: aplicaciones web y móviles a medida, productos con IA y transformación digital integral, con proyectos destacados en fintech para TV, quioscos de restaurante y apps Smart TV.",
      descriptionAr: "AllSoftware24 شريك موثوق لحلول البرمجيات المبتكرة: تطبيقات ويب وجوال مخصصة ومنتجات مدعومة بالذكاء الاصطناعي وتحول رقمي شامل، مع مشاريع رائدة في fintech للتلفزيون وأنظمة الكشك والتطبيقات الذكية.",
      technologies: ["React", "Node.js", "TypeScript", "React Native", "AI/ML", "PostgreSQL", "Cloud Infrastructure"],
      year: "2024",
      link: "https://allsoftware24.com/"
    },
    {
      id: 10,
      title: "TimeForFight",
      titleTr: "TimeForFight",
      titleFr: "TimeForFight",
      titleEs: "TimeForFight",
      titleAr: "TimeForFight",
      category: "Sports Tech",
      categoryTr: "Spor Teknolojisi",
      categoryFr: "Tech Sportive",
      categoryEs: "Tecnología Deportiva",
      categoryAr: "تكنولوجيا الرياضة",
      image: timeForFightImage,
      description: "TimeForFight is tournament software for combat sports — registrations, seeded brackets, mat scheduling, weigh-in, live scoring, results and rankings in one system. Built for athletes, coaches, organizers and referees so you run the fight, not the chaos.<br/><br/>• Live mat control, brackets that redraw, and season rankings<br/>• Offline-first referee scoring that syncs when Wi‑Fi returns<br/>• Club registration in one cart, QR weigh-in and check-in<br/>• Supports BJJ, Judo, Wrestling, Karate, Taekwondo, MMA, Kickboxing and Grappling",
      descriptionTr: "TimeForFight, dövüş sporları için turnuva yazılımıdır — kayıtlar, seed'li bracket'ler, mat planlaması, tartı, canlı skorlama, sonuçlar ve sıralamalar tek platformda. Sporcular, antrenörler, organizatörler ve hakemler için tasarlandı; kaosu değil dövüşü yönetin.<br/><br/>• Canlı mat kontrolü, yeniden çizilen bracket'ler ve sezon sıralamaları<br/>• Wi‑Fi kesilince cihazda kaydedilen, bağlantı gelince senkronlanan hakem skorlaması<br/>• Kulüp kaydı tek sepetten, QR tartı ve check-in<br/>• BJJ, Judo, Güreş, Karate, Taekwondo, MMA, Kickboks ve Grappling desteği",
      descriptionFr: "TimeForFight est un logiciel de tournoi pour sports de combat : inscriptions, tableaux seedés, planning des mats, pesée, scoring en direct, résultats et classements dans un seul système. Conçu pour athlètes, coachs, organisateurs et arbitres.",
      descriptionEs: "TimeForFight es software de torneos para deportes de combate: inscripciones, brackets sembrados, programación de mats, pesaje, puntuación en vivo, resultados y rankings en una sola plataforma. Para atletas, entrenadores, organizadores y árbitros.",
      descriptionAr: "TimeForFight برنامج بطولات لرياضات القتال: التسجيلات والأقواس المجدولة وجدولة الحصائر والوزن والتسجيل المباشر والنتائج والترتيب في نظام واحد. مخصص للرياضيين والمدربين والمنظمين والحكام.",
      technologies: ["Next.js", "React", "TypeScript", "Real-time Sync", "Offline-first", "Payments", "QR Check-in"],
      year: "2026",
      link: "https://timeforfight.com"
    }
  ], []) as ProjectType[];

  // Featured project
  const featuredProject: ProjectType = {
    id: 1,
    title: "Trade on tv",
    titleTr: "Trade on tv",
    titleFr: "Trade on tv",
    titleEs: "Trade on tv",
    titleAr: "Trade on tv",
    category: "FinTech",
    categoryTr: "FinTek",
    categoryFr: "FinTech",
    categoryEs: "FinTech",
    categoryAr: "التكنولوجيا المالية",
    image: tradeOnTVImage,
    description: "The world's first finance-focused Smart TV app. It offers traders on-screen trading power with AI-powered analytics.<br/><br/>• Trade on tv is the world's first smart TV app that brings financial information to the TV.<br/>• Investors can now track stocks, cryptocurrencies, foreign exchange and indices in real time.<br/>• Get AI-powered analysis and manage portfolios on screen.<br/><br/><strong>Usage Areas:</strong><br/>• Investment analysis in Smart TVs<br/>• Graphically supported market tracking<br/>• Access to financial information with voice command<br/>• Simple interface that the whole family can use together at home<br/><br/><strong>Global goal:</strong><br/>To make investment manageable from the center of the home, not just from mobile devices.",
    descriptionTr: "Uygulamamız, kullanıcıların borsa ve kripto para birimlerinin mevcut durumunu doğrudan televizyonlarından takip etmelerini sağlar. Kullanıcılar, favori hisse senetlerini ve kripto para birimlerini seçebilir, piyasa hareketlerini gerçek zamanlı olarak izleyebilir ve önemli finansal göstergelere erişebilirler. Kullanıcı dostu arayüzü ile uygulamamız, televizyonlarını kullanarak borsa ve kripto para piyasalarını takip etmek isteyen kullanıcılara sorunsuz bir deneyim sunmak için tasarlanmıştır. Favori hisse senetlerini ve kripto para birimlerini takip etmek, gerçek zamanlı haberlere erişmek, yatırımcıların bilinçli finansal kararlar vermesine yardımcı olur.",
    descriptionFr: "La première application Smart TV axée sur la finance au monde. Elle offre aux traders une puissance de trading à l'écran avec des analyses alimentées par l'IA.<br/><br/>• Trade on tv est la première application de télévision intelligente au monde qui apporte des informations financières à la télévision.<br/>• Les investisseurs peuvent désormais suivre les actions, les crypto-monnaies, les changes et les indices en temps réel.<br/>• Obtenez des analyses alimentées par l'IA et gérez des portefeuilles à l'écran.<br/><br/><strong>Domaines d'utilisation:</strong><br/>• Analyse d'investissement sur les Smart TVs<br/>• Suivi de marché avec support graphique<br/>• Accès aux informations financières par commande vocale<br/>• Interface simple que toute la famille peut utiliser ensemble à la maison<br/><br/><strong>Objectif mondial:</strong><br/>Rendre l'investissement gérable depuis le centre de la maison, pas seulement depuis les appareils mobiles.",
    descriptionEs: "La primera aplicación Smart TV del mundo centrada en las finanzas. Ofrece a los operadores poder de negociación en pantalla con análisis impulsados por IA.<br/><br/>• Trade on tv es la primera aplicación de televisión inteligente del mundo que lleva información financiera al televisor.<br/>• Los inversores ahora pueden seguir acciones, criptomonedas, divisas e índices en tiempo real.<br/>• Obtenga análisis impulsados por IA y administre carteras en pantalla.<br/><br/><strong>Áreas de uso:</strong><br/>• Análisis de inversión en Smart TVs<br/>• Seguimiento de mercado con soporte gráfico<br/>• Acceso a información financiera mediante comando de voz<br/>• Interfaz simple que toda la familia puede usar junta en casa<br/><br/><strong>Objetivo global:</strong><br/>Hacer que la inversión sea manejable desde el centro del hogar, no solo desde dispositivos móviles.",
    descriptionAr: "أول تطبيق تلفزيون ذكي في العالم يركز على التمويل. يوفر للمتداولين قوة تداول على الشاشة مع تحليلات مدعومة بالذكاء الاصطناعي.<br/><br/>• Trade on tv هو أول تطبيق تلفزيون ذكي في العالم يجلب المعلومات المالية إلى التلفزيون.<br/>• يمكن للمستثمرين الآن تتبع الأسهم والعملات المشفرة وصرف العملات والمؤشرات في الوقت الحقيقي.<br/>• احصل على تحليلات مدعومة بالذكاء الاصطناعي وإدارة المحافظ على الشاشة.<br/><br/><strong>مجالات الاستخدام:</strong><br/>• تحليل الاستثمار في أجهزة التلفزيون الذكية<br/>• تتبع السوق بدعم رسومي<br/>• الوصول إلى المعلومات المالية بالأوامر الصوتية<br/>• واجهة بسيطة يمكن للعائلة بأكملها استخدامها معًا في المنزل<br/><br/><strong>الهدف العالمي:</strong><br/>جعل الاستثمار قابلاً للإدارة من مركز المنزل، وليس فقط من الأجهزة المحمولة.",
    technologies: ["React", "Node.js", "WebSocket", "TradingView API", "OAuth 2.0", "Smart TV SDKs", "Secure Payment Gateway"],
    year: "2023",
    link: "https://tradeontv.com/"
  };
  
  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };
  
  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.5
      }
    }
  };
  
  // Translations object
  const translations = {
    en: {
      pageTitle: "Our Services & Projects",
      pageSubtitle: "We deliver cutting-edge software solutions across various platforms and technologies, with a special focus on TV applications, kiosk solutions, and digital transformation.",
      viewProject: "View Project",
      exploreProject: "Explore Project",
      featuredProjectTitle: "Featured Project",
      allCategory: "All",
      year: "Year"
    },
    tr: {
      pageTitle: "Hizmetlerimiz & Projelerimiz",
      pageSubtitle: "Çeşitli platformlarda ve teknolojilerde, özellikle TV uygulamaları, kiosk çözümleri ve dijital dönüşüm alanlarında yenilikçi yazılım çözümleri sunuyoruz.",
      viewProject: "Projeyi Görüntüle",
      exploreProject: "Projeyi Keşfet",
      featuredProjectTitle: "Öne Çıkan Proje",
      allCategory: "Tümü",
      year: "Yıl"
    },
    fr: {
      pageTitle: "Nos Services & Projets",
      pageSubtitle: "Nous proposons des solutions logicielles de pointe sur diverses plateformes et technologies, avec un accent particulier sur les applications TV, les solutions de kiosque et la transformation numérique.",
      viewProject: "Voir le Projet",
      exploreProject: "Explorer le Projet",
      featuredProjectTitle: "Projet en Vedette",
      allCategory: "Tous",
      year: "Année"
    },
    es: {
      pageTitle: "Nuestros Servicios & Proyectos",
      pageSubtitle: "Ofrecemos soluciones de software de vanguardia en diversas plataformas y tecnologías, con un enfoque especial en aplicaciones de TV, soluciones de quiosco y transformación digital.",
      viewProject: "Ver Proyecto",
      exploreProject: "Explorar Proyecto",
      featuredProjectTitle: "Proyecto Destacado",
      allCategory: "Todos",
      year: "Año"
    },
    ar: {
      pageTitle: "خدماتنا ومشاريعنا",
      pageSubtitle: "نقدم حلول برمجية متطورة عبر منصات وتقنيات متنوعة، مع تركيز خاص على تطبيقات التلفزيون وحلول الكشك والتحول الرقمي.",
      viewProject: "عرض المشروع",
      exploreProject: "استكشاف المشروع",
      featuredProjectTitle: "المشروع المميز",
      allCategory: "الكل",
      year: "السنة"
    }
  };
  
  // Grid order: Trade on TV + flagship sites first row, then remaining projects
  const displayedProjects = useMemo(() => {
    const order = [9, 8, 10, 1, 2, 3, 4, 5, 6, 7];
    return order
      .map((id) => projectsData.find((project) => project.id === id))
      .filter((project): project is ProjectType => Boolean(project));
  }, [projectsData]);
  
  return (
    <ProjectsSection>
      <SEO 
        title={seoTitle}
        description={seoDescription}
        keywords={seoKeywords}
      />
      <ProjectsContainer>
        <ProjectsHeader>
          <PageTitle
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            {language === 'tr' ? translations.tr.pageTitle :
             language === 'fr' ? translations.fr.pageTitle :
             language === 'es' ? translations.es.pageTitle :
             language === 'ar' ? translations.ar.pageTitle :
             translations.en.pageTitle}
          </PageTitle>
          <PageSubtitle
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            {language === 'tr' ? translations.tr.pageSubtitle :
             language === 'fr' ? translations.fr.pageSubtitle :
             language === 'es' ? translations.es.pageSubtitle :
             language === 'ar' ? translations.ar.pageSubtitle :
             translations.en.pageSubtitle}
          </PageSubtitle>
        </ProjectsHeader>
        
        {/* Featured Project */}
        <FeaturedProjectSection>
          <SectionTitle>
            {language === 'tr' ? translations.tr.featuredProjectTitle :
             language === 'fr' ? translations.fr.featuredProjectTitle :
             language === 'es' ? translations.es.featuredProjectTitle :
             language === 'ar' ? translations.ar.featuredProjectTitle :
             translations.en.featuredProjectTitle}
          </SectionTitle>
          <FeaturedProject>
            <FeaturedProjectContent>
              <FeaturedProjectInfo>
                <FeaturedProjectTitle>
                  {language === 'tr' && featuredProject.titleTr ? featuredProject.titleTr :
                   language === 'fr' && featuredProject.titleFr ? featuredProject.titleFr :
                   language === 'es' && featuredProject.titleEs ? featuredProject.titleEs :
                   language === 'ar' && featuredProject.titleAr ? featuredProject.titleAr :
                   featuredProject.title}
                </FeaturedProjectTitle>
                <FeaturedProjectDescription dangerouslySetInnerHTML={{ __html: 
                  language === 'tr' && featuredProject.descriptionTr ? featuredProject.descriptionTr :
                  language === 'fr' && featuredProject.descriptionFr ? featuredProject.descriptionFr :
                  language === 'es' && featuredProject.descriptionEs ? featuredProject.descriptionEs :
                  language === 'ar' && featuredProject.descriptionAr ? featuredProject.descriptionAr :
                  featuredProject.description }} />
                <FeaturedProjectTechnologies>
                  {featuredProject.technologies.map((tech, index) => (
                    <TechnologyTag key={index}>{tech}</TechnologyTag>
                  ))}
                </FeaturedProjectTechnologies>
                <div style={{ display: 'flex', gap: '15px' }}>
                  <FeaturedProjectLink
                    href={featuredProject.link || '#'}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {language === 'tr' ? translations.tr.exploreProject :
                     language === 'fr' ? translations.fr.exploreProject :
                     language === 'es' ? translations.es.exploreProject :
                     language === 'ar' ? translations.ar.exploreProject :
                     translations.en.exploreProject}
                  </FeaturedProjectLink>
                  
                  <WatchButton 
                    onClick={handlePlayVideo}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M5 3L19 12L5 21V3Z" fill="white" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    Watch NOW
                  </WatchButton>
                </div>
              </FeaturedProjectInfo>
              <FeaturedProjectImageContainer>
                <img src={featuredProject.image} alt={featuredProject.title} />
              </FeaturedProjectImageContainer>
            </FeaturedProjectContent>
          </FeaturedProject>
        </FeaturedProjectSection>
        
        {/* Projects Grid */}
        <AnimatePresence mode="wait">
          <ProjectsGrid
            key="projects-grid"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            exit="hidden"
          >
            {displayedProjects.map((project) => (
            <ProjectCard
                key={project.id}
                variants={itemVariants}
                whileHover={{ y: -10 }}
              >
                {project.id === 1 && (
                  <ProjectImageContainer1>
                    <ProjectImage src={project.image} alt={project.title} />
                  </ProjectImageContainer1>
                )}
                {project.id === 2 && (
                  <ProjectImageContainer2>
                    <ProjectImage src={project.image} alt={project.title} />
                  </ProjectImageContainer2>
                )}
                {project.id === 3 && (
                  <ProjectImageContainer3>
                    <ProjectImage src={project.image} alt={project.title} />
                  </ProjectImageContainer3>
                )}
                {project.id === 4 && (
                  <ProjectImageContainer4>
                    <ProjectImage src={project.image} alt={project.title} />
                  </ProjectImageContainer4>
                )}
                {project.id === 5 && (
                  <ProjectImageContainer5>
                    <ProjectImage src={project.image} alt={project.title} />
                  </ProjectImageContainer5>
                )}
                {project.id === 6 && (
                  <ProjectImageContainer6>
                    <ProjectImage src={project.image} alt={project.title} />
                  </ProjectImageContainer6>
                )}
                {project.id === 7 && (
                  <ProjectImageContainer7>
                    <ProjectImage src={project.image} alt={project.title} />
                  </ProjectImageContainer7>
                )}
                {project.id === 8 && (
                  <ProjectImageContainer8>
                    <ProjectImage src={project.image} alt={project.title} />
                  </ProjectImageContainer8>
                )}
                {project.id === 9 && (
                  <ProjectImageContainer9>
                    <ProjectImage src={project.image} alt={project.title} />
                  </ProjectImageContainer9>
                )}
                {project.id === 10 && (
                  <ProjectImageContainer10>
                    <ProjectImage src={project.image} alt={project.title} />
                  </ProjectImageContainer10>
                )}
              <ProjectContent>
                  <ProjectTitle>
                    {language === 'tr' && project.titleTr ? project.titleTr :
                     language === 'fr' && project.titleFr ? project.titleFr :
                     language === 'es' && project.titleEs ? project.titleEs :
                     language === 'ar' && project.titleAr ? project.titleAr :
                     project.title}
                  </ProjectTitle>
                  <ProjectDescription dangerouslySetInnerHTML={{ __html: 
                    language === 'tr' && project.descriptionTr ? project.descriptionTr :
                    language === 'fr' && project.descriptionFr ? project.descriptionFr :
                    language === 'es' && project.descriptionEs ? project.descriptionEs :
                    language === 'ar' && project.descriptionAr ? project.descriptionAr :
                    project.description }} />
                  <ProjectTechnologies>
                    {project.technologies.map((tech, index) => (
                      <TechnologyTag key={index}>{tech}</TechnologyTag>
                    ))}
                  </ProjectTechnologies>
                  {project.link && (
                    <FeaturedProjectLink
                      href={project.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      {language === 'tr' ? translations.tr.exploreProject :
                       language === 'fr' ? translations.fr.exploreProject :
                       language === 'es' ? translations.es.exploreProject :
                       language === 'ar' ? translations.ar.exploreProject :
                       translations.en.exploreProject}
                    </FeaturedProjectLink>
                  )}
              </ProjectContent>
            </ProjectCard>
          ))}
          </ProjectsGrid>
      </AnimatePresence>
      
      {/* Video Container */}
      <HeroVideoContainer isVisible={showVideo}>
        {/* Video Controls */}
        <VideoControls>
          <VideoCloseBtn onClick={handleCloseVideo}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M18 6L6 18" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M6 6L18 18" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </VideoCloseBtn>
          <VolumeControl>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M11 5L6 9H2V15H6L11 19V5Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M15.54 8.46C16.4774 9.39764 17.004 10.6692 17.004 11.995C17.004 13.3208 16.4774 14.5924 15.54 15.53" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <input 
              type="range" 
              min="0" 
              max="1" 
              step="0.1" 
              defaultValue="0.7" 
              onChange={handleVolumeChange} 
            />
          </VolumeControl>
          <QualitySelector value={quality} onChange={handleQualityChange}>
            <option value="1080p">1080p</option>
            <option value="720p">720p</option>
            <option value="360p">360p</option>
          </QualitySelector>
        </VideoControls>
        <VideoElement 
          ref={videoRef}
          src={videoSources[quality]}
          playsInline
          autoPlay={showVideo}
          muted={false}
          controls={false}
          preload="none"
          loop={false}
          disablePictureInPicture 
          disableRemotePlayback
        />
      </HeroVideoContainer>
    </ProjectsContainer>
    </ProjectsSection>
  );
}

export default Projects; 